import Vue, { ComputedRef, computed } from 'vue';
import { serverProps } from '@/Common/Environment/const';

import { ADDON_TYPE, ADDONS_MAP } from '@/HostApp/services/SystemLayout/Addons';
import { createVueTemplate } from '@/HostApp/ui/templates/vueTemplate';
import { createDojoTemplate } from '@/HostApp/ui/templates/legacyTemplate';

import { State, Getter } from 'vuex-simple';

export enum Thresholds {
	xs = 0,
	sm = 600,
	md = 1360,
	lg = 1936,
	xl = 2576
}

const DEFAULT_TEMPLATE_ID = 'default#default';

export class SystemLayoutModule {
	constructor() {
		const thresholds = Object.keys(Thresholds).filter((key) => !Number(key) && key !== '0') as Array<
			keyof typeof Thresholds
		>;
		this.thresholds = thresholds.sort((a, b) => Thresholds[a] - Thresholds[b]);

		const screenSizes = Object.keys(Thresholds) as Array<string | number>;
		this.screenSizes = screenSizes.filter((size) => Number(size) || size === '0') as Array<number>;

		this.addMediaListeners();
		this.registerAddon(ADDONS_MAP[ADDON_TYPE.VIEWPORT]);
		this.registerAddon(ADDONS_MAP[ADDON_TYPE.GLOBAL_LOADING]);

		const workspaceDefaultTemplate = serverProps._vueWorkspace
			? createVueTemplate(serverProps)
			: createDojoTemplate(serverProps);

		this.registerLayoutTemplate({ id: DEFAULT_TEMPLATE_ID, layoutTemplate: workspaceDefaultTemplate });
	}

	@State()
	public thresholds;

	@State()
	private screenSizes;

	@State()
	public screenSize: keyof typeof Thresholds = 'xs';

	@State()
	public addons: Common.IMap<LAYOUT.IGridItem> = {};

	@State()
	protected layoutTemplates: Common.IMap<LAYOUT.ILayoutTemplate> = {};

	public registerLayoutTemplate({
		id,
		layoutTemplate
	}: {
		id: string;
		layoutTemplate: LAYOUT.ILayoutTemplate;
	}): LAYOUT.ILayoutTemplate | undefined {
		if (id && layoutTemplate) {
			Vue.set(this.layoutTemplates, id, layoutTemplate);
		}

		return this.layoutTemplates[id];
	}

	@Getter()
	protected get activeView(): string {
		return window.aps.context._view.id;
	}

	public registerAddon(addon: LAYOUT.IGridItem): void {
		if (!this.addons[addon.name]) {
			Vue.set(this.addons, addon.name, addon);
		}
	}

	public unregisterAddon(addon: LAYOUT.IGridItem): void {
		if (this.addons[addon.name]) {
			delete this.addons[addon.name];
		}
	}

	protected calcScreenSize(): keyof typeof Thresholds {
		let newScreenSize = 'xs';
		this.screenSizes.forEach((size) => {
			const mediaQuery = matchMedia(`(min-width: ${size}px)`);

			if (mediaQuery.matches) {
				newScreenSize = Thresholds[size];
			}
		});
		return newScreenSize as keyof typeof Thresholds;
	}

	private readonly setNewScreenSize = (): void => {
		this.screenSize = this.calcScreenSize();
	};

	protected addMediaListeners(): void {
		this.screenSizes.forEach((size) => {
			const mediaQuery = matchMedia(`(min-width: ${size}px)`);
			mediaQuery.addEventListener('change', this.setNewScreenSize);
		});

		this.setNewScreenSize();
	}

	@Getter()
	public get currentLayoutTemplate() {
		const [appId, viewId] = this.activeView.split('#');

		return (
			this.layoutTemplates[`${appId}#${viewId}`] ||
			this.layoutTemplates[appId] ||
			this.layoutTemplates[DEFAULT_TEMPLATE_ID]
		);
	}
}

const systemLayoutModule = new SystemLayoutModule();

function smartAssign<T extends object>(source1: T, source2: T) {
	const result = Object.assign({}, source1 || {}) as T;
	Object.keys(source2).forEach(
		(key) => source2[key as keyof T] && (result[key as keyof T] = source2[key as keyof T])
	);

	return result;
}

export const useLayoutTemplate = (): { grid: ComputedRef<LAYOUT.IGrid>; items: ComputedRef<LAYOUT.IGridItem[]> } => {
	return {
		grid: computed(() => {
			return systemLayoutModule.currentLayoutTemplate.grid;
		}),
		items: computed(() => {
			return systemLayoutModule.currentLayoutTemplate.items;
		})
	};
};

export const useComputedStyle = (item: LAYOUT.IGrid | LAYOUT.IGridItem): ComputedRef<Partial<CSSStyleDeclaration>> => {
	return computed(() => {
		const mixedStyle = Object.assign({}, item?.styles?.style || {});

		return systemLayoutModule.thresholds
			.filter((key) => Thresholds[key] <= Thresholds[systemLayoutModule.screenSize])
			.reduce((acc, key) => {
				const mix = (item?.styles && item.styles[key]) || {};

				return smartAssign(acc, mix);
			}, mixedStyle);
	});
};

export default systemLayoutModule;
